import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogClose,
} from '@/components/ui/dialog'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { CustomerPreview } from '@/hooks/useCustomersPreviewFilter'
import { useState } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import SurveyResponseCard from './SurveyResponseCard'
import RecordResponseDialog from '@/modules/Survey/components/dialogs/RecordResponseDialog'
import {
    CreateSurveyRecordPayload,
    RecordResponse,
} from '@/modules/Survey/types'
import { Grid2X2, List } from 'lucide-react'
import SurveyResponseTableView from './SurveyResponseTableView'
import { cn } from '@/lib/utils'

interface SurveyResponseTableProps {
    customers: CustomerPreview[]
    responses: CreateSurveyRecordPayload[]
    onResponsesChanged: (payload: CreateSurveyRecordPayload) => void
    onRemoveCustomer?: (customerId: number) => void
}

const SurveyResponseTable = ({
    customers,
    responses,
    onResponsesChanged,
    onRemoveCustomer,
}: SurveyResponseTableProps) => {
    const { t } = useTranslation('surveys')

    // View State
    const [view, setView] = useState<'grid' | 'table'>('table')

    // Dialog State
    const [editingCell, setEditingCell] = useState<{
        customerId: number
        customerName: string
    } | null>(null)

    const [customerToDelete, setCustomerToDelete] = useState<number | null>(
        null
    )

    // Open Dialog
    const handleCellClick = (customer: CustomerPreview) => {
        setEditingCell({
            customerId: customer.customer_id,
            customerName: customer.customer_name || 'Unknown',
        })
    }

    // Save Dialog
    const handleDialogSave = (newResponses: RecordResponse) => {
        if (!editingCell) return

        const { customerId } = editingCell

        const createPayload: CreateSurveyRecordPayload = {
            customer_id: customerId,
            records: newResponses,
        }
        onResponsesChanged(createPayload)
        setEditingCell(null)
    }

    const handleConfirmDelete = () => {
        if (customerToDelete !== null && onRemoveCustomer) {
            onRemoveCustomer(customerToDelete)
            setCustomerToDelete(null)
        }
    }

    const initialResponses = editingCell
        ? responses.find((r) => r.customer_id === editingCell.customerId)
              ?.records
        : ({} as RecordResponse)

    return (
        <div className="space-y-4 bg-white p-4">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium">
                    {t('surveys.responses.title', 'Responses')}
                </h3>
                <div
                    className="flex items-center border rounded-md p-1 bg-white"
                    data-tour="survey-view-toggle"
                >
                    <Button
                        variant="ghost"
                        size="sm"
                        className={cn(
                            'min-w-0 h-8 px-2',
                            view === 'table' && 'bg-blue-100 text-blue-600'
                        )}
                        onClick={() => setView('table')}
                    >
                        <List className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        className={cn(
                            'min-w-0 h-8 px-2',
                            view === 'grid' && 'bg-blue-100 text-blue-600'
                        )}
                        onClick={() => setView('grid')}
                    >
                        <Grid2X2 className="h-4 w-4" />
                    </Button>
                </div>
            </div>

            {customers.length === 0 ? (
                <Card className="p-8 text-center border-dashed">
                    <p className="text-muted-foreground">
                        {t(
                            'surveys.responses.no_customers',
                            'No customers found'
                        )}
                    </p>
                </Card>
            ) : view === 'table' ? (
                <SurveyResponseTableView
                    customers={customers}
                    responses={responses}
                    onResponsesChanged={onResponsesChanged}
                    onRemoveCustomer={setCustomerToDelete}
                />
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
                    {customers.map((customer) => {
                        const response = responses.find(
                            (r) => r.customer_id === customer.customer_id
                        )
                        const responded = !!response
                        return (
                            <SurveyResponseCard
                                data-tour="survey-response-card"
                                key={customer.customer_id}
                                customer={customer}
                                responded={responded}
                                response={response?.records}
                                onRemove={() =>
                                    setCustomerToDelete(customer.customer_id)
                                }
                                onClick={handleCellClick}
                            />
                        )
                    })}
                </div>
            )}

            <RecordResponseDialog
                open={!!editingCell}
                onOpenChange={(open) => !open && setEditingCell(null)}
                customerName={editingCell?.customerName}
                initialResponses={initialResponses}
                onSave={handleDialogSave}
            />

            <Dialog
                open={customerToDelete !== null}
                onOpenChange={(open) => !open && setCustomerToDelete(null)}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {t(
                                'surveys.delete_confirm.title',
                                'Confirm deletion'
                            )}
                        </DialogTitle>
                        <DialogDescription>
                            <Trans
                                t={t}
                                i18nKey="surveys.delete_confirm.description"
                                values={{
                                    name:
                                        customers.find(
                                            (c) =>
                                                c.customer_id ===
                                                customerToDelete
                                        )?.customer_name || '',
                                }}
                                components={[<strong className="font-bold" />]}
                                defaults="Are you sure you want to remove customer <0>{{name}}</0> from the list? All entered responses will be lost."
                            />
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="outline" type="button">
                                {t('common.cancel', 'Cancel')}
                            </Button>
                        </DialogClose>
                        <Button
                            variant="destructive"
                            onClick={handleConfirmDelete}
                        >
                            {t('common.delete', 'Delete')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}

export default SurveyResponseTable
